import { SG_EVENT_NAMES } from '@/utils/tools/segment/eventNames';
import { trackEvent } from '@/utils/tools/segment/tracking';
import { Avatar, Button, Dropdown, Flex, Form, Input, Modal, Select, Space, Switch, Table, Typography } from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import useSwr from 'swr';
const { Option } = Select;
const { Text, Link } = Typography;

export default function BrandShareModal({ brand, open, onClose, onChange }) {
   const [publicShare, setPublicShare] = useState(false);
   const creator = useSwr(`/api/user/${brand?.creator}/public/profile`);

   useEffect(() => {
      setPublicShare(brand?.public);
   }, [brand]);

   async function handlePublicShareChange() {
      setPublicShare(!publicShare);
      await axios.patch('/api/brands/' + brand.id, { isPublic: !brand.public }).then((res) => {});
      onChange();
   }

   async function handleUpdatePermission(user, permission) {
      if (permission === 'remove') {
         const newSharedWith = brand.shared_with.filter((item) => item.email !== user.email);
         await axios.patch('/api/brands/' + brand.id, { shared_with: newSharedWith }).then((res) => {
            if (res.statusText === 'OK') {
               onChange();
               brand.shared_with = newSharedWith;
            }
         });
         return;
      }

      const newSharedWith = brand.shared_with.map((item) => {
         if (item.email === user.email) {
            return { ...item, permission: permission };
         }
         return item;
      });
      await axios.patch('/api/brands/' + brand.id, { shared_with: newSharedWith }).then((res) => {
         if (res.statusText === 'OK') {
            onChange();
            brand.shared_with = newSharedWith;
         }
      });
   }

   async function inviteByEmail(value) {
      // check if email is already shared
      const isShared = brand.shared_with.find((item) => item.email === value.email);
      if (isShared) {
         return;
      }

      const newSharedWith = brand.shared_with.concat({ email: value.email, permission: value.permission });
      await axios.patch('/api/brands/' + brand.id, { shared_with: newSharedWith }).then((res) => {
         if (res.statusText === 'OK') {
            onChange();
            brand.shared_with = newSharedWith;
            trackEvent(SG_EVENT_NAMES.USER_SHARE_BRAND, {
               brandName: brand.name,
               brandCreatorEmail: creator?.data?.email,
               sharedEmail: value.email,
               sharedPermission: value.permission,
            });
         }
      });
   }

   const permissionsMenu = [
      { key: 'read', label: 'View' },
      { key: 'write', label: 'Edit' },
      { type: 'divider' },
      { key: 'remove', label: 'Remove' },
   ];

   const shareData = [
      {
         key: 0,
         avatar: `/api/user/public/avatar?email=${creator?.data?.email}`,
         email: creator?.data?.email,
         permission: 'creator',
      },
   ].concat(
      brand?.shared_with?.map((item, index) => {
         return {
            key: index + 1,
            avatar: `/api/user/public/avatar?email=${item.email}`,
            email: item.email,
            permission: item.permission,
         };
      })
   );
   const shareColumns = [
      {
         title: 'Avatar',
         dataIndex: 'avatar',
         key: 'avatar',
         render: (text) => <Avatar size="small" src={text} />,
      },
      {
         title: 'Email',
         dataIndex: 'email',
         key: 'email',
      },
      {
         title: 'Permission',
         dataIndex: 'permission',
         key: 'permission',
         width: 120,
         render: (text, record) => (
            <>
               {record.permission === 'creator' && <Text type="secondary">Creator</Text>}
               {record.permission !== 'creator' && (
                  <Dropdown
                     menu={{
                        items: permissionsMenu,
                        selectedKeys: [text],
                        selectable: true,
                        onSelect: (item) => {
                           handleUpdatePermission(record, item.key);
                        },
                     }}
                     trigger={['click']}
                  >
                     <Button type="text" size="small" block>
                        <Flex align="center" justify="space-between" gap={32} style={{ width: '100%' }}>
                           {text === 'read' ? 'View only' : text === 'write' ? 'Can edit' : text === 'undifined'}
                           <i className="fi fi-rr-angle-small-down" />
                        </Flex>
                     </Button>
                  </Dropdown>
               )}
            </>
         ),
      },
   ];

   if (!brand) return null;

   return (
      <>
         <Modal title="Share Brand" open={open} onCancel={onClose} footer={null} width={600} destroyOnClose>
            <Flex vertical gap={32}>
               {/* Share by email */}
               <Flex gap={32}>
                  <Form layout="inline" style={{ width: '100%' }} onFinish={inviteByEmail} id="inviteByEmail">
                     <Space.Compact style={{ width: '100%' }} size="large">
                        <Form.Item name="email" noStyle rules={[{ required: true, message: 'Please input email!' }]}>
                           <Input placeholder="Search user" size="large" />
                        </Form.Item>
                        <Form.Item name="permission" noStyle initialValue="read">
                           <Select defaultValue="view" style={{ width: 120 }}>
                              <Option value="read">View</Option>
                              <Option value="write">Edit</Option>
                           </Select>
                        </Form.Item>
                     </Space.Compact>
                  </Form>
                  <Button size="large" type="primary" style={{ width: 110 }} form="inviteByEmail" htmlType="submit">
                     Invite
                  </Button>
               </Flex>

               {/* Share by link */}
               <Flex vertical gap={32}>
                  <Flex align="center" justify="space-between" gap={32}>
                     <Text strong>Share by public link</Text>
                     <Switch checked={publicShare} onChange={() => handlePublicShareChange()} />
                  </Flex>

                  {publicShare && (
                     <Space direction="vertical" size={4}>
                        <Link type="link" copyable>
                           {`${window.location.origin}/brands/${brand.id}/preview`}
                        </Link>
                        <Text type="secondary">Your brand is now public, anyone with this link can view it. </Text>
                     </Space>
                  )}
               </Flex>

               {/* Shared with */}
               <Flex vertical gap={32}>
                  <Text strong>Shared with</Text>

                  <Table
                     size="small"
                     showHeader={false}
                     pagination={false}
                     dataSource={shareData}
                     columns={shareColumns}
                     variant="borderless"
                  />
               </Flex>
            </Flex>
         </Modal>
      </>
   );
}
